import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import { JurisdictionCascadeDialog } from "@/components/ui/jurisdiction-cascade-dialog";
import LoadingScreen from "@/components/ui/loading-screen";
import { toast } from "@/components/ui/sonner";
import { Switch } from "@/components/ui/switch";
import { updateLicenseType } from "@/hera-api/client";
import { getLicenseTypesOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type { LicenseTypeResponseSchema } from "@/hera-api/client/types.gen";
import { useLicenseTypeJurisdictionCascade } from "@/hooks/use-license-type-jurisdiction-cascade";
import { sortLicenseTypesByDisplayOrder } from "@/lib/license-type-utils";
import { getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Copy, Plus } from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { DashboardTable } from "./dashboard-table";
import { LicenseTypeDetailsSheet } from "./license-type-details-sheet";

// Helper function to convert technical error messages to user-friendly ones
const convertToUserFriendlyError = (errorMessage: string): string => {
  if (!errorMessage) return "An error occurred";

  const message = errorMessage.toLowerCase();

  // Handle license type name conflicts
  if (message.includes("license type name") && message.includes("already")) {
    if (message.includes("reactivate")) {
      return "License type name conflict: Cannot reactivate because a license type with this name is already active";
    }
    return "License type name conflict: This name is already in use";
  }

  // Handle other constraint violations
  if (
    message.includes("unique constraint") ||
    message.includes("already exists")
  ) {
    return "License type name conflict: This name is already in use";
  }

  // Handle database constraint violations
  if (
    message.includes("constraint violation") ||
    message.includes("database constraint")
  ) {
    return "Data conflict: The information provided conflicts with existing data";
  }

  // Return original message if no pattern matches
  return errorMessage;
};

// Define accessor keys for license types
export type LicenseTypeAccessorKeys =
  | "id"
  | "name"
  | "requires_property"
  | "effective_date_label"
  | "effective_end_date_label"
  | "active"
  | "under_development";

export const LICENSE_TYPE_ACCESSOR_KEYS: LicenseTypeAccessorKeys[] = [
  "id",
  "name",
  "requires_property",
  "effective_date_label",
  "effective_end_date_label",
  "active",
  "under_development",
];

// Define column properties for license types
type LicenseTypeColumnProperties = {
  displayName: string;
  defaultVisible: boolean;
  getValue: (
    row: LicenseTypeResponseSchema,
    accessorKey: LicenseTypeAccessorKeys,
  ) => string;
  sortingFn?: (rowA: any, rowB: any) => number;
  renderCell?: (row: LicenseTypeResponseSchema) => ReactNode;
  /** When false, Blank and Not blank are hidden because every row has a value. */
  allowBlankFilter?: boolean;
};

export function LicenseConfigurationPanel() {
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedLicenseTypeId, setSelectedLicenseTypeId] = useState<
    string | null
  >(null);
  const [duplicateLicenseTypeData, setDuplicateLicenseTypeData] =
    useState<any>(null);
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<{
    licenseTypeId: string;
    licenseTypeName: string;
    isActive: boolean;
  } | null>(null);
  const queryClient = useQueryClient();

  // Hook for configuration cascade functionality
  const {
    activeConfigurationsCount,
    deletedConfigurationsCount,
    isLoadingCounts,
    cascadeConfigurations,
    isCascading,
  } = useLicenseTypeJurisdictionCascade({
    licenseTypeId: pendingToggle?.licenseTypeId,
    enabled: !!pendingToggle,
  });

  const {
    data: licenseTypesData,
    isLoading,
    error,
  } = useQuery(getLicenseTypesOptions({ query: { include_deleted: true } }));

  // Mutation for toggling license type active status
  const toggleActiveMutation = useMutation({
    mutationFn: async ({
      licenseTypeId,
      isActive,
      clearUnderDevelopment = false,
    }: {
      licenseTypeId: string;
      isActive: boolean;
      clearUnderDevelopment?: boolean;
    }) => {
      const body: { is_deleted: boolean; under_development?: boolean } = {
        is_deleted: isActive, // If currently active, set to deleted (true), if inactive, set to not deleted (false)
      };

      // When reactivating, clear under_development flag
      if (!isActive && clearUnderDevelopment) {
        body.under_development = false;
      }

      const response = await updateLicenseType({
        path: { license_type_id: licenseTypeId },
        body,
      });

      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch the license types query
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions({ query: { include_deleted: true } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions().queryKey,
      });

      toast({
        title: "Success",
        description: `License type has been ${variables.isActive ? "deactivated" : "reactivated"} successfully.`,
      });
    },
    onError: (error) => {
      const errorMessage = getQueryErrorMessage(error);
      const userFriendlyMessage = convertToUserFriendlyError(errorMessage);

      toast({
        title: "License Type Update Failed",
        description: userFriendlyMessage,
        variant: "destructive",
      });
    },
  });

  // Reorder license types (swap display_order with adjacent row)
  const reorderLicenseTypesMutation = useMutation({
    mutationFn: async ({
      idA,
      idB,
      orderA,
      orderB,
    }: {
      idA: string;
      idB: string;
      orderA: number;
      orderB: number;
    }) => {
      const r1 = await updateLicenseType({
        path: { license_type_id: idA },
        body: { display_order: orderB },
      });
      if (r1.error) throw r1.error;
      const r2 = await updateLicenseType({
        path: { license_type_id: idB },
        body: { display_order: orderA },
      });
      if (r2.error) throw r2.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions({ query: { include_deleted: true } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions().queryKey,
      });
      toast({
        title: "Order updated",
        description: "License type order has been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to reorder",
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  // Mutation for toggling under_development status
  const toggleUnderDevelopmentMutation = useMutation({
    mutationFn: async ({
      licenseTypeId,
      underDevelopment,
    }: { licenseTypeId: string; underDevelopment: boolean }) => {
      const response = await updateLicenseType({
        path: { license_type_id: licenseTypeId },
        body: { under_development: underDevelopment },
      });

      if (response.error) {
        throw response.error;
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions({ query: { include_deleted: true } })
          .queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: getLicenseTypesOptions().queryKey,
      });

      toast({
        title: variables.underDevelopment
          ? "License Type Marked as Under Development"
          : "License Type Activated",
        description: variables.underDevelopment
          ? "This license type is now marked as under development."
          : "This license type has been activated and is no longer under development.",
      });
    },
    onError: (error) => {
      const errorMessage = getQueryErrorMessage(error);
      const userFriendlyMessage = convertToUserFriendlyError(errorMessage);

      toast({
        title: "License Type Update Failed",
        description: userFriendlyMessage,
        variant: "destructive",
      });
    },
  });

  const handleRowClick = useCallback((licenseTypeId: string) => {
    setSelectedLicenseTypeId(licenseTypeId);
    setIsEditSheetOpen(true);
  }, []);

  const handleAddNewLicenseType = useCallback(() => {
    setSelectedLicenseTypeId(null);
    setIsEditSheetOpen(true);
  }, []);

  // Updated handler that shows cascade dialog before toggling
  const handleToggleActive = useCallback(
    async (licenseTypeId: string, isActive: boolean) => {
      // Find the license type name for display
      const licenseType = licenseTypesData?.license_types?.find(
        (lt) => lt.id === licenseTypeId,
      );

      if (!licenseType) return;

      // Set the pending toggle data
      setPendingToggle({
        licenseTypeId,
        licenseTypeName: licenseType.name,
        isActive,
      });

      // Check if there are any jurisdictions to cascade
      const affectedCount = isActive
        ? activeConfigurationsCount
        : deletedConfigurationsCount;

      // If no jurisdictions are affected, proceed directly without showing dialog
      if (affectedCount === 0) {
        try {
          await toggleActiveMutation.mutateAsync({ licenseTypeId, isActive });
          setPendingToggle(null);
        } catch (error) {
          setPendingToggle(null);
        }
      } else {
        // Show dialog if there are jurisdictions to cascade
        setCascadeDialogOpen(true);
      }
    },
    [
      licenseTypesData?.license_types,
      activeConfigurationsCount,
      deletedConfigurationsCount,
      toggleActiveMutation,
    ],
  );

  // Handler for confirming the cascade action
  const handleConfirmCascade = useCallback(async () => {
    if (!pendingToggle) return;

    const { licenseTypeId, isActive } = pendingToggle;

    try {
      // First update the license type status
      await toggleActiveMutation.mutateAsync({ licenseTypeId, isActive });

      // Then cascade to jurisdictions if there are any to update
      const affectedCount = isActive
        ? activeConfigurationsCount
        : deletedConfigurationsCount;
      if (affectedCount > 0) {
        await cascadeConfigurations({ isDeleted: isActive });
      }

      // Close dialog and reset state
      setCascadeDialogOpen(false);
      setPendingToggle(null);
    } catch (error) {
      // Error handling is done in the mutations themselves
      // Just close the dialog and reset state
      setCascadeDialogOpen(false);
      setPendingToggle(null);
    }
  }, [
    pendingToggle,
    toggleActiveMutation,
    activeConfigurationsCount,
    deletedConfigurationsCount,
    cascadeConfigurations,
  ]);

  // Handle duplicate
  const handleDuplicate = useCallback(
    (licenseTypeId: string) => {
      const licenseType = licenseTypesData?.license_types?.find(
        (lt) => lt.id === licenseTypeId,
      );
      if (licenseType) {
        // Transform API data to form data format (same as in use-license-type-form.ts)
        const duplicateData = {
          name: licenseType.name || "",
          requires_property: licenseType.requires_property || false,
          effective_date_label: licenseType.effective_date_label || "",
          effective_end_date_label: licenseType.effective_end_date_label || "",
          is_deleted: false, // Always set to active when copying
        };
        setDuplicateLicenseTypeData(duplicateData);
        setSelectedLicenseTypeId(null); // Ensure it's treated as new
        setIsEditSheetOpen(true);
      }
    },
    [licenseTypesData?.license_types],
  );

  const getLicenseTypeColumnProperties = useCallback(
    (accessorKey: LicenseTypeAccessorKeys): LicenseTypeColumnProperties => {
      switch (accessorKey) {
        case "id":
          return {
            displayName: "ID",
            defaultVisible: false,
            allowBlankFilter: false,
            getValue: (row) => row.id,
          };
        case "name":
          return {
            displayName: "Name",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => row.name,
          };
        case "requires_property":
          return {
            displayName: "Requires Property",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => (row.requires_property ? "Yes" : "No"),
          };
        case "effective_date_label":
          return {
            displayName: "Effective Date Label",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => row.effective_date_label,
          };
        case "effective_end_date_label":
          return {
            displayName: "Effective End Date Label",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => row.effective_end_date_label,
          };
        case "active":
          return {
            displayName: "Active",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => (!row.deleted_at ? "Yes" : "No"),
            renderCell: (row) => {
              const isRowActive = row.deleted_at === null;
              return (
                <span
                  className="flex items-center"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Switch
                    checked={isRowActive}
                    disabled={toggleActiveMutation.isPending}
                    aria-label={
                      isRowActive
                        ? "Deactivate license type"
                        : "Reactivate license type"
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        void toggleActiveMutation.mutateAsync({
                          licenseTypeId: row.id,
                          isActive: false,
                          clearUnderDevelopment: true,
                        });
                      } else {
                        void handleToggleActive(row.id, true);
                      }
                    }}
                  />
                </span>
              );
            },
          };
        case "under_development":
          return {
            displayName: "Under Development",
            defaultVisible: true,
            allowBlankFilter: false,
            getValue: (row) => (row.under_development ? "Yes" : "No"),
            renderCell: (row) => {
              const isRowActive = row.deleted_at === null;
              return (
                <span
                  className="flex items-center"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Switch
                    checked={row.under_development}
                    disabled={
                      !isRowActive || toggleUnderDevelopmentMutation.isPending
                    }
                    aria-label="Under development"
                    onCheckedChange={(underDevelopment) => {
                      toggleUnderDevelopmentMutation.mutate({
                        licenseTypeId: row.id,
                        underDevelopment,
                      });
                    }}
                  />
                </span>
              );
            },
          };
        default:
          return {
            displayName: "Unknown",
            defaultVisible: false,
            getValue: () => "",
          };
      }
    },
    [handleToggleActive, toggleActiveMutation, toggleUnderDevelopmentMutation],
  );

  const sortedLicenseTypes = useMemo(
    () => sortLicenseTypesByDisplayOrder(licenseTypesData?.license_types ?? []),
    [licenseTypesData?.license_types],
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 flex-col min-w-0">
        <div className="flex-1 p-6">
          <ErrorComponent
            title="Failed to load license types"
            message="There was an error loading the license types. Please try again."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col min-w-0">
      <div className="flex min-h-0 flex-1 flex-col min-w-0 p-6">
        <div className="flex shrink-0 justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            {/* Left side is empty for now */}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={handleAddNewLicenseType}>
              <span className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add License Type
              </span>
            </Button>
          </div>
        </div>

        <DashboardTable<LicenseTypeResponseSchema, LicenseTypeAccessorKeys>
          data={sortedLicenseTypes}
          onRowClick={handleRowClick}
          accessorKeys={LICENSE_TYPE_ACCESSOR_KEYS}
          getColumnProperties={getLicenseTypeColumnProperties}
          getRowId={(row) => row.id}
          tablePersistenceKey="non-registrant-license-types"
          enableSorting={false}
          enableColumnFilters={false}
          leadingActionsHeaderLabel="Reorder"
          renderRowLeadingActions={(row) => {
            const idx = sortedLicenseTypes.findIndex((lt) => lt.id === row.id);
            const canMoveUp = idx > 0;
            const canMoveDown = idx >= 0 && idx < sortedLicenseTypes.length - 1;
            return (
              <div className="flex flex-col gap-0.5 items-center justify-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label="Move license type up"
                  disabled={!canMoveUp || reorderLicenseTypesMutation.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    const prev = sortedLicenseTypes[idx - 1];
                    if (!prev) return;
                    reorderLicenseTypesMutation.mutate({
                      idA: row.id,
                      idB: prev.id,
                      orderA: row.display_order ?? 0,
                      orderB: prev.display_order ?? 0,
                    });
                  }}
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  aria-label="Move license type down"
                  disabled={
                    !canMoveDown || reorderLicenseTypesMutation.isPending
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = sortedLicenseTypes[idx + 1];
                    if (!next) return;
                    reorderLicenseTypesMutation.mutate({
                      idA: row.id,
                      idB: next.id,
                      orderA: row.display_order ?? 0,
                      orderB: next.display_order ?? 0,
                    });
                  }}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            );
          }}
          renderRowActions={(row) => (
            <div className="flex flex-wrap justify-end gap-2 items-center">
              <Button
                variant="outline"
                size="sm"
                aria-label="Duplicate license type"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicate(row.id);
                }}
                className="flex-1 sm:flex-none min-w-[80px]"
              >
                <Copy className="h-4 w-4" />
                <span className="ml-1">Duplicate</span>
              </Button>
            </div>
          )}
        />
      </div>

      <LicenseTypeDetailsSheet
        isOpen={isEditSheetOpen}
        onOpenChange={(open) => {
          setIsEditSheetOpen(open);
          if (!open) setDuplicateLicenseTypeData(null);
        }}
        licenseTypeId={selectedLicenseTypeId}
        duplicateLicenseTypeData={duplicateLicenseTypeData}
        key={
          selectedLicenseTypeId ||
          (duplicateLicenseTypeData ? "duplicate" : "new")
        }
      />

      <JurisdictionCascadeDialog
        isOpen={cascadeDialogOpen}
        onOpenChange={setCascadeDialogOpen}
        licenseTypeName={pendingToggle?.licenseTypeName || ""}
        isDeactivating={pendingToggle?.isActive || false}
        affectedConfigurationsCount={
          pendingToggle?.isActive
            ? activeConfigurationsCount
            : deletedConfigurationsCount
        }
        isLoading={
          isLoadingCounts || isCascading || toggleActiveMutation.isPending
        }
        onConfirm={handleConfirmCascade}
      />
    </div>
  );
}
